/**
 * Central repository for all strings used in the app
 * This file serves as a single source of truth for all text content
 * In the future, strings could be loaded from a backend API
 */

import { logger } from '../utils/logger';

// Interface for the string repository structure
export interface StringRepository {
  common: {
    buttonLabels: {
      login: string;
      loginLoading: string;
      signup: string;
      logout: string;
      continue: string;
      confirm: string;
      cancel: string;
      done: string;
      showAll: string;
      goBack: string;
      tryAgain: string;
      ok: string;
      yes: string;
      no: string;
      save: string;
      send: string;
      share: string;
      join: string;
      leave: string;
      message: string;
      back: string;
      post: string;
    };
    navigationLabels: {
      back: string;
      next: string;
      cancel: string;
    };
    inputLabels: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    };
    placeholders: {
      tapToAddPhoto: string;
      selectGroup: string;
      reportReason: string;
      gatheringPhotoOptional: string;
    };
    termsOfService: string;
    somethingWentWrong: string;
    tryAgain: string;
    loading: string;
    error: {
      userNotFound: string;
      updateFailed: string;
      linkOpenFailed: string;
      invalidUrl: string;
      errorLoadingPage: string;
      title: string;
      errorLoadingEvent: string;
      somethingWentWrongEventDetails: string;
      genericMessage: string;
    };
    connect: string;
    chat: string;
    events: string;
    marketplace: string;
    alerts: {
      cancelGathering: {
        title: string;
        message: string;
      };
    };
  };
  onboarding: {
    welcome: {
      headline: string;
      subhead: string;
      benefits: {
        gatherings: string;
        recommendations: string;
        marketplace: string;
      };
      conductSummary: string;
      // Split around the tappable link so the consent line reads as one sentence.
      termsConsentPrefix: string;
      termsConsentLink: string;
      termsConsentSuffix: string;
      smsConsent: string;
      cta: string;
      ctaLoading: string;
      error: string;
    };
    // NOTE: the terms modal's copy deliberately lives in `legalDocuments.ts`, not
    // here — it stays English in every locale because the documents themselves are
    // only published in English. See LEGAL_MODAL_COPY.
  };
  introduction: {
    title: string;
    subtitle: string;
    photoSection: {
      title: string;
      uploadText: string;
      editText: string;
      actionSheetTitle: string;
      options: {
        chooseFromLibrary: string;
        takePhoto: string;
        cancel: string;
      };
    };
    introText: string;
    placeholder: string;
    buttonLabels: {
      next: string;
      nextLoading: string;
      skip: string;
    };
    errors: {
      uploadFailed: string;
      somethingWentWrong: string;
      cameraPermissionDenied: string;
      photoLibraryPermissionDenied: string;
      calendarPermissionDenied: string;
    };
  };
  perks: {
    title: string;
    filters: {
      all: string;
      saved: string;
    };
    states: {
      error: {
        title: string;
        subtitle: string;
      };
      empty: {
        allPerks: string;
        savedPerks: string;
      };
    };
    perkCard: {
      tapToSeeDetails: string;
    };
    detail: {
      title: string;
      enjoyedPerk: string;
      actions: {
        saved: string;
        saveToList: string;
        learnMore: string;
        redeem: string;
        save: string;
        email: string;
        phone: string;
        redeemed: string;
      };
    };
  };
  auth: {
    login: {
      title: string;
      emailPlaceholder: string;
      passwordPlaceholder: string;
      forgotPassword: string;
      needSignup: string;
      slogan: string;
    };
    signup: {
      title: string;
      namePlaceholder: string;
      emailPlaceholder: string;
      passwordPlaceholder: string;
      haveAccount: string;
      inputLabels: {
        firstName: string;
        lastName: string;
        email: string;
        buildingCode: string;
        unit: string;
        phoneNumber: string;
        password: string;
        repeatPassword: string;
      };
      checkboxLabels: {
        agreeToTerms: string;
        ageConfirmation: string;
        smsConsent: string;
      };
      helpText: {
        buildingCode: string;
        buildingCodeVerified: string;
        phoneVerification: string;
        leaseEmailRequirement: string;
      };
      errors: {
        invalidBuildingCode: string;
        emailTaken: string;
        emailCheckError: string;
        phoneTaken: string;
        phoneCheckError: string;
        signupError: string;
      };
    };
    passwordReset: {
      title: string;
      subtitle: string;
      emailPlaceholder: string;
      buttonText: string;
      buttonLoadingText: string;
      successMessage: string;
      errorMessage: string;
    };
    verification: {
      title: string;
      subtitle: string;
      verifying: string;
      noCode: string;
      resend: string;
      codeSent: string;
      wrongPhone: string;
      updateNumber: string;
      updatePhoneTitle: string;
      updatePhoneSubtitle: string;
      phoneNumberPlaceholder: string;
      phoneUpdated: string;
      updating: string;
      update: string;
      errors: {
        invalidCode: string;
        verificationFailed: string;
        sendCodeFailed: string;
        invalidPhoneFormat: string;
      };
    };
    deactivatedUser: {
      title: string;
      mainText: string;
      description: string;
      problem: {
        heading: string;
        body: string;
      };
      why: {
        heading: string;
        body: string;
      };
      resolve: {
        heading: string;
        body: string;
      };
      contactSupport: string;
      supportEmail: string;
      emailSubject: string;
    };
  };
  home: {
    welcomeMessage: string;
  };
  groups: {
    loadingMessage: string;
    myGroups: string;
    allGroupsOpenToResidents: string;
    otherGroups: string;
    allGroups: string;
    emptyState: {
      title: string;
      subtitle: string;
    };
    card: {
      memberCount: {
        singular: string;
        plural: string;
      };
      eventCount: {
        singular: string;
        plural: string;
      };
      newBadge: string;
    };
    suggest: {
      title: string;
      subtitle: string;
      cardCta: string;
      form: {
        groupName: {
          label: string;
          placeholder: string;
          helpText: string;
        };
        description: {
          label: string;
          placeholder: string;
          helpText: string;
        };
        additionalDetails: {
          label: string;
          placeholder: string;
          helpText: string;
        };
      };
      buttons: {
        submit: string;
        submitting: string;
        cancel: string;
        backToApp: string;
      };
      success: {
        title: string;
        message: string;
      };
      chat: {
        robotName: string;
        responses: {
          step1: string;
          step2Excellent: string;
          step2Question: string;
          step2Note: string;
          step3Question: string;
          step4Fabulous: string;
        };
        placeholders: {
          groupName: string;
          groupDescription: string;
          extraDetails: string;
        };
        errorResponse: string;
      };
      errors: {
        submitFailed: string;
        groupExists: string;
        networkError: string;
      };
    };
    info: {
      title: string;
      about: string;
      members: string;
      pastGatherings: string;
      showMore: string;
      showLess: string;
      noDescription: string;
      noAdditionalInfo: string;
      communityWide: string;
      openToAllResidents: string;
      buttons: {
        join: string;
        joining: string;
        leave: string;
        leaving: string;
      };
      leaveConfirm: {
        title: string;
        message: string;
      };
      errors: {
        joinFailed: string;
        leaveFailed: string;
        loadInfoFailed: string;
      };
    };
    errors: {
      loadFailed: string;
      refreshFailed: string;
    };
  };
  chat: {
    navigation: {
      fallbackTitle: string;
      profile: string;
      newMessage: string;
      channelList: string;
      directMessages: string;
    };
    errors: {
      unableToLoadChat: string;
      streamInitFailed: string;
      channelLoadFailed: string;
      unableToLoadProfile: string;
      messageSendFailed: string;
    };
    analytics: {
      source: string;
    };
    channelList: {
      title: string;
      noChannels: string;
      loading: string;
      refresh: string;
      newMessage: string;
      error: string;
      chatNotAvailable: string;
      failedToOpenConversation: string;
    };
    channel: {
      typing: string;
      messageInputPlaceholder: string;
      sendButton: string;
      reactions: {
        like: string;
        love: string;
        smile: string;
        laugh: string;
        clap: string;
      };
      webFallback: {
        title: string;
        message: string;
      };
      loading: {
        conversation: string;
      };
      bannedUser: {
        warning: string;
      };
      reportUser: {
        title: string;
        notImplementedMessage: string;
      };
      alerts: {
        channelMuted: string;
        channelUnmuted: string;
      };
      menu: {
        muteConversation: string;
        unmuteConversation: string;
        blockUser: string;
        unblockUser: string;
      };
    };
    actions: {
      report: string;
      block: string;
      unblock: string;
      delete: string;
      edit: string;
      reply: string;
      muteUser: string;
      unmuteUser: string;
    };
  };
  discussion: {
    welcomeMessage: {
      greeting: string;
    };
    allMembers: {
      title: string;
      subtitle: string;
    };
    alerts: {
      titles: {
        error: string;
        success: string;
      };
      error: {
        deleteComment: string;
        flagComment: string;
        blockUser: string;
      };
      success: {
        flagComment: string;
        blockUser: string;
      };
    };
    commentActions: {
      deleteConfirm: {
        title: string;
        message: string;
      };
      blockConfirm: {
        title: string;
        message: string;
      };
      flagConfirm: {
        title: string;
        message: string;
      };
      labels: {
        deleteComment: string;
        blockUser: string;
        reportUser: string;
        flagPost: string;
        cancel: string;
      };
    };
    title: string;
    emptyState: {
      title: string;
      subtitle: string;
      createFirstPost: string;
    };
    loadingMessage: string;
    comments: {
      singular: string;
      plural: string;
    };
    buttons: {
      addComment: string;
      createNewPost: string;
      reply: string;
      edit: string;
      delete: string;
      report: string;
      pin: string;
      unpin: string;
      showMore: string;
      showLess: string;
      loadMore: string;
      loadOlder: string;
      seeMoreReplies: string;
      hideReplies: string;
    };
    actions: {
      commenting: string;
      replying: string;
      loading: string;
      posting: string;
      deleting: string;
      reporting: string;
    };
    reactions: {
      like: string;
      love: string;
      clap: string;
      smile: string;
      laugh: string;
      addReaction: string;
      viewReactions: string;
      react: string;
      allReactions: string;
      reactionCount: {
        singular: string;
        plural: string;
      };
    };
    compose: {
      placeholder: string;
      replyPlaceholder: string;
      characterLimit: string;
      imageLimit: string;
      addImage: string;
      removeImage: string;
    };
    validation: {
      commentEmpty: string;
      commentNotFound: string;
    };
    success: {
      commentCreated: string;
      replyCreated: string;
      commentUpdated: string;
      commentDeleted: string;
      commentPinned: string;
      commentUnpinned: string;
      commentFlagged: string;
    };
    marketplace: {
      detectedTitle: string;
      detectedMessage: string;
      confirmYes: string;
      confirmNo: string;
      composeButton: string;
      composeHeader: string;
      composePlaceholder: string;
    };
    errors: {
      loadFailed: string;
      createFailed: string;
      updateFailed: string;
      deleteFailed: string;
      reactionFailed: string;
      reportFailed: string;
      networkError: string;
      permissionDenied: string;
      moderationRequired: string;
      commentTooLong: string;
      tooManyImages: string;
      invalidContent: string;
      togglePinFailed: string;
      flagCommentFailed: string;
    };
    moderation: {
      flaggedContent: string;
      moderationRequired: string;
      contentRemoved: string;
      userBlocked: string;
      reportSubmitted: string;
      imageHiddenTooltip: string;
    };
    moderationAlerts: {
      profanity: {
        title: string;
        message: string;
        options: {
          viewCodeOfConduct: string;
          editPost: string;
        };
      };
      sentiment: {
        title: string;
        message: string;
        options: {
          sendAnyway: string;
          viewCodeOfConduct: string;
          editPost: string;
        };
      };
      flaggedTermsHeader: string;
    };
    timeAgo: {
      justNow: string;
      minuteAgo: string;
      minutesAgo: string;
      hourAgo: string;
      hoursAgo: string;
      dayAgo: string;
      daysAgo: string;
      weekAgo: string;
      weeksAgo: string;
    };
    accessibility: {
      commentBy: string;
      replyTo: string;
      reactToComment: string;
      moreOptions: string;
      hidePinnedComment: string;
      expandReplies: string;
      collapseReplies: string;
    };
  };
  groupMembers: {
    title: string;
    loading: string;
    viewAll: string;
    memberCount: {
      singular: string;
      plural: string;
    };
    errors: {
      loadFailed: string;
      networkError: string;
    };
  };
  tooltip: {
    loading: string;
    ctaPressed: string;
    defaultCta: string;
    errors: {
      loadFailed: string;
      actionFailed: string;
    };
  };
  publicProfile: {
    loading: string;
    membershipAge: {
      anHour: string;
      onCobu: string;
      hour: string;
      hours: string;
      day: string;
      days: string;
      month: string;
      months: string;
      year: string;
      years: string;
    };
    buttons: {
      manageProfile: string;
      message: string;
      block: string;
      report: string;
      cancel: string;
    };
    sections: {
      about: string;
      skills: string;
      skillsSubtitle: string;
      helpingHand: string;
      needHelp: string;
      groups: string;
      groupsInCommon: string;
      alsoPart: string;
    };
    actions: {
      reportUser: string;
      blockUser: string;
      blockConfirmTitle: string;
      blockConfirmMessage: string;
      userBlocked: string;
      blockingUser: string;
      reportSubmitted: string;
    };
    errors: {
      loadFailed: string;
      blockFailed: string;
      reportFailed: string;
      userNotFound: string;
    };
    accessibility: {
      profileImage: string;
      moreOptions: string;
      closeModal: string;
    };
    imageModal: {
      closeLabel: string;
      profileAlt: string;
    };
  };
  groupDetail: {
    navigation: {
      loading: string;
      groupNotFound: string;
    };
    actions: {
      joinGroup: string;
      joining: string;
      leaving: string;
      joined: string;
      composeComment: string;
      replyToComment: string;
      createNewPost: string;
    };
    labels: {
      communityWideGroup: string;
      gatherings: string;
      new: string;
      now: string;
      upcoming: string;
      createGathering: string;
      gatheringsEmptyTitle: string;
      gatheringsEmptySubtitle: string;
      gatheringStatus: {
        past: string;
        live: string;
        upcoming: string;
      };
      gatheringTime: {
        today: string;
        tomorrow: string;
      };
      attendance: {
        singular: string;
        plural: string;
        youreGoing: string;
      };
    };
    states: {
      errorTitle: string;
      somethingWentWrong: string;
      groupNotFound: string;
      groupNotFoundDescription: string;
      tryAgain: string;
      ok: string;
    };
    errors: {
      joinFailed: string;
      leaveFailed: string;
    };
    analytics: {
      groupJoinedSuccessfully: string;
      groupLeftSuccessfully: string;
      leaveGroupInitiated: string;
    };
  };
  forceUpdate: {
    title: string;
    subtitle: string;
  };
  maintenanceMode: {
    title: string;
    subtitle: string;
  };
  codePush: {
    progressMessage: string;
  };
  webView: {
    defaultTitle: string;
    surveyFallbackTitle: string;
    errors: {
      loadFailed: string;
      mailAppFailed: string;
      phoneAppFailed: string;
      messagingAppFailed: string;
      noUrl: string;
    };
  };
  universalButton: {
    errors: {
      linkOpenFailed: string;
      noReferralOffer: string;
    };
  };
  disabledOverlay: {
    defaultOverlayText: string;
    defaultLoadingText: string;
  };
  ui: {
    allMembers: {
      noResultsSearch: string;
      noResults: string;
      membersLabel: string;
      externalLabel: string;
      directMessages: {
        unavailable: string;
        notAvailable: string;
        unableToOpen: string;
        tryAgain: string;
      };
      noMembersSearch: string;
      noRsvp: string;
    };
    imagePicker: {
      permissionRequired: string;
      permissionRequiredMessage: string;
      goToSettings: string;
      addPhoto: string;
      openGallery: string;
      openCamera: string;
      removePhoto: string;
      choosAnOption: string;
      uploading: string;
      errors: {
        pickImageFailed: string;
        takePictureFailed: string;
      };
      placeholders: {
        tapToAddPhoto: string;
      };
    };
    calendar: {
      openSettings: string;
      eventTitleFallback: string;
      addToCalendar: string;
      addingToCalendar: string;
      addedToCalendar: string;
    };
    dateTimePicker: {
      selectDate: string;
      selectTime: string;
      selectDateTime: string;
      selectDateTimeTitle: string;
      cancel: string;
      confirm: string;
      chooseDate: string;
      chooseTime: string;
    };
    imageViewer: {
      closeLabel: string;
    };
    userAvatar: {
      profileAlt: string;
    };
    pullToRefresh: {
      pull: string;
      release: string;
      refreshing: string;
    };
    universalButton: {
      fallbackTitle: string;
    };
    modal: {
      searchPeople: string;
    };
    members: {
      unknownUser: string;
      guest: string;
      guests: string;
    };
    roleBadge: {
      communityManager: string;
      communityManagerShort: string;
      buildingAdmin: string;
      buildingAdminShort: string;
    };
    floatingButton: {
      addComment: string;
      addEvent: string;
    };
    reportUser: {
      userReported: string;
      userReportedMessage: string;
      title: string;
      cancel: string;
      submit: string;
      submitError: string;
      validation: {
        reasonRequired: string;
        reasonTooLong: string;
      };
    };
    groupPicker: {
      title: string;
      closeLabel: string;
      loading: string;
      error: string;
      failedToLoad: string;
      noGroupsFound: string;
    };
    googleReviewModal: {
      title: string;
      description: string;
      buttons: {
        writeReview: string;
        skip: string;
      };
      errors: {
        title: string;
        unableToOpenLink: string;
      };
    };
  };
  hooks: {
    publicProfile: {
      errors: {
        loadProfileFailed: string;
        alreadyBlocked: string;
        blockFailed: string;
        userNotAvailable: string;
      };
      success: {
        userBlocked: string;
      };
    };
    perkDetail: {
      errors: {
        userNotAvailable: string;
        redeemFailed: string;
        saveFailed: string;
      };
    };
    imagePickerHook: {
      permissionNeeded: string;
    };
    eventDetail: {
      errors: {
        rsvpFailed: string;
        eventFull: string;
        updateRsvpFailed: string;
        capacityReached: string;
        cancelRsvpFailed: string;
        deleteCommentFailed: string;
        cancelGatheringFailed: string;
        blockUserFailed: string;
      };
    };
    managePublicProfile: {
      errors: {
        userNotFound: string;
        updateFailed: string;
      };
      success: {
        profileUpdated: string;
      };
    };
  };
  events: {
    error: {
      title: string;
      message: string;
      buttonText: string;
    };
    detail: {
      fallbackTitle: string;
      buttons: {
        findGatherings: string;
        rsvp: string;
        payToRsvp: string;
        expressInterest: string;
        register: string;
        attending: string;
        interested: string;
        change: string;
        rsvpNow: string;
        creating: string;
        updating: string;
        createEvent: string;
        updateEvent: string;
        checkedIn: string;
        addGuests: string;
        minus: string;
        plus: string;
        confirm: string;
        cancel: string;
        updateRegistration: string;
        updateRsvp: string;
        confirmCheckin: string;
        confirmRegistration: string;
        completeRsvp: string;
      };
      alerts: {
        eventCancelled: string;
        eventCancelledMessage: string;
        eventEnded: string;
        eventEndedMessage: string;
        rsvpOptions: string;
        rsvpOptionsMessage: string;
        cancelRSVP: string;
        cannotAttend: string;
        changeGuestCount: string;
        invalidCount: string;
        invalidCountMessage: string;
        editEvent: string;
        editNotImplemented: string;
        deleteComment: string;
      };
      labels: {
        locationTBD: string;
        justNow: string;
        rsvpWillOpenSoon: string;
        eventFull: string;
        live: string;
        gatheringCancelled: string;
        residentOrganized: string;
        gatheringCreator: string;
        attendingCount: string;
        userRsvpConfirmation: string;
        whatToBring: string;
        residentOrganizedDisclaimer: string;
        about: string;
        attendeesCount: string;
        guestSingular: string;
        guestPlural: string;
        moreAttendeeSingular: string;
        moreAttendeePlural: string;
        howManyGuests: string;
        eventEnded: string;
        checkInGreetingHeading: string;
        bringingGuestsQuestion: string;
        guestsWithYouQuestion: string;
        noMoreGuestsAllowed: string;
        cancelConfirmation: string;
        recurringIndicator: string;
        priceFrom: string;
        directions: string;
        drivingDirections: string;
        close: string;
        findingComment: string;
      };
      descriptionSuffix: {
        rsvp: string;
        expressInterest: string;
      };
    };
    create: {
      headerTitles: {
        create: string;
        edit: string;
      };
      labels: {
        selectGroup: string;
        gatheringName: string;
        gatheringDateTime: string;
        location: string;
        description: string;
        whatToBring: string;
        totalAttendees: string;
        maxGuestsPerRSVP: string;
        eventPhoto: string;
      };
      placeholders: {
        chooseGroup: string;
        gatheringName: string;
        dateTime: string;
        location: string;
        description: string;
        whatToBring: string;
        totalAttendees: string;
        maxGuests: string;
      };
      errors: {
        selectGroup: string;
        selectDateTime: string;
        createFailed: string;
        updateFailed: string;
      };
      success: {
        title: string;
        eventCreated: string;
        eventUpdated: string;
      };
    };
    menu: {
      editDetails: string;
      cancelGathering: string;
      reportGatheringAuthor: string;
      blockGatheringAuthor: string;
      cancel: string;
    };
    empty: {
      buttonText: string;
      attendingTitle: string;
      attendingSubtitle: string;
      pastTitle: string;
      pastSubtitle: string;
      upcomingTitle: string;
      upcomingSubtitle: string;
      discoverGroups: string;
    };
    comments: {
      title: string;
      addComment: string;
      commentsCount: string;
      loading: string;
      noComments: string;
      deletedComment: string;
      timeFormat: {
        justNow: string;
        minutesAgo: string;
        hoursAgo: string;
      };
    };
    validation: {
      title: {
        required: string;
        tooLong: string;
      };
      startTime: {
        required: string;
        invalid: string;
      };
      location: {
        required: string;
        tooLong: string;
      };
      description: {
        required: string;
        tooShort: string;
      };
      maxEventAttendees: {
        required: string;
        mustBeNumber: string;
        mustBeInteger: string;
        minimum: string;
        maximum: string;
      };
      maxGuests: {
        required: string;
        mustBeNumber: string;
        mustBeInteger: string;
        minimum: string;
        maximum: string;
        mustBeLessThanAttendees: string;
      };
    };
    feedback: {
      header: {
        thanksForOrganizing: string;
        thanksForComing: string;
      };
      rating: {
        sectionTitle: string;
        excellent: string;
        good: string;
        meh: string;
      };
      comment: {
        sectionTitle: string;
      };
      buttons: {
        didNotAttend: string;
        submit: string;
      };
    };
    filters: {
      upcoming: string;
      attending: string;
      past: string;
    };
    list: {
      sectionTitle: string;
      item: {
        rsvpNow: string;
        attending: string;
        happeningNow: string;
        cancelled: string;
        personSingularAttending: string;
        peoplePluralAttending: string;
        personSingularAttended: string;
        peoplePluralAttended: string;
        youAnd: string;
      };
    };
  };
  referral: {
    tabs: {
      offerDetails: string;
      yourResults: string;
    };
    yourResults: {
      title: string;
      sections: {
        stats: {
          title: string;
          heading: string;
          subtitle: string;
        };
        customize: {
          title: string;
          heading: string;
          subtitle: string;
        };
      };
    };
    stats: {
      labels: {
        leads: string;
        views: string;
        leases: string;
      };
      loading: string;
      errorMessage: string;
      yourLeads: string;
      tableHeaders: {
        who: string;
        dateReferred: string;
      };
    };
    customize: {
      loading: string;
      errorMessage: string;
      description: string;
      descriptionPrefix: string;
      descriptionSuffix: string;
      previewPage: string;
      sectionTitle: string;
      includeProfilePicture: string;
      updateProfileImage: string;
      welcomeMessage: string;
      welcomeMessagePlaceholder: string;
      previewPageTitle: string;
      alertTitles: {
        success: string;
        error: string;
      };
      messages: {
        profileAdded: string;
        profileRemoved: string;
        profileUpdateFailed: string;
        previewFailed: string;
        welcomeMessageSaved: string;
        welcomeMessageSaveFailed: string;
      };
    };
    detail: {
      webViewTitle: string;
      shareTitle: string;
      shareMessage: string;
      loadingMessage: string;
      errorTitle: string;
      alertTitle: string;
      buttons: {
        sharing: string;
        shareLink: string;
        referNow: string;
        goBack: string;
      };
      errors: {
        shareError: string;
        shareFailure: string;
        referFormError: string;
        loadError: string;
      };
      success: {
        shareCopied: string;
      };
      empty: {
        title: string;
        subtitle: string;
      };
    };
    offer: {
      rewardSingle: string;
      rewardDouble: string;
      termsHeading: string;
    };
  };
  notifications: {
    title: string;
    emptyState: string;
    loadOlder: string;
    markAllAsRead: {
      title: string;
      message: string;
      confirm: string;
      cancel: string;
    };
    groups: {
      today: string;
      yesterday: string;
      older: string;
    };
    messages: {
      allMarkedAsRead: string;
      markAllFailed: string;
      markSingleFailed: string;
    };
    templates: {
      postedInGroupHtml: string;
      postedInFeedHtml: string;
      neighborPostedInFeed: string;
      gatheringCreatedHtml: string;
      reactionToCommentHtml: string;
      commentOnGatheringHtml: string;
      gatheringCanceledHtml: string;
      gatheringTimeChangedHtml: string;
      gatheringDateCancelledHtml: string;
      gatheringDateCancelledWithNoteHtml: string;
      gatheringReminderHtml: string;
      newGroupCreatedHtml: string;
      groupProposalApprovedHtml: string;
      directMessageReceived: string;
      referralOfferCreated: string;
      newPerkActivatedHtml: string;
    };
  };
  review: {
    prompt: {
      loading: string;
      error: string;
      notAvailable: string;
    };
    fallback: {
      title: string;
      message: string;
      buttons: {
        writeReview: string;
        cancel: string;
      };
    };
    success: {
      prompted: string;
      completed: string;
    };
    errors: {
      eligibilityCheckFailed: string;
      updateStatusFailed: string;
      promptFailed: string;
      fallbackFailed: string;
    };
  };
  profile: {
    header: {
      fallbackName: string;
    };
    sections: {
      account: string;
      helpAndSettings: string;
    };
    menuItems: {
      manageAccount: string;
      pleaseVerifyAccount: string;
      chatWithCobu: string;
      support: string;
      manageNotifications: string;
      blockedUsers: string;
      contactUs: string;
      reviewBuilding: string;
      residentPortal: string;
      buildingContactInfo: string;
      logOut: string;
    };
    switches: {
      showFullName: string;
    };
    alerts: {
      logout: {
        title: string;
        message: string;
        cancel: string;
        confirm: string;
      };
      error: {
        title: string;
        message: string;
      };
      success: {
        title: string;
        message: string;
      };
      comingSoon: {
        title: string;
        message: string;
      };
    };
    loading: {
      loadingNotifications: string;
    };
    errors: {
      unableToLoadNotifications: string;
      checkConnectionAndRetry: string;
      tapToRetry: string;
      noNotificationTypesAvailable: string;
      contactSupportIfIssuePersists: string;
      failedToUpdateNotificationSettings: string;
      failedToToggleDirectMessagePushNotifications: string;
    };
    notifications: {
      title: string;
      subtitle: string;
    };
    changePassword: {
      title: string;
      newPassword: {
        label: string;
        placeholder: string;
      };
      confirmPassword: {
        label: string;
        placeholder: string;
      };
      passwordRequirements: {
        title: string;
        requirements: string[];
      };
      alerts: {
        validationError: string;
        success: string;
        error: string;
      };
    };
    manageAccount: {
      title: string;
      firstName: {
        label: string;
        placeholder: string;
      };
      lastName: {
        label: string;
        placeholder: string;
      };
      email: {
        label: string;
        placeholder: string;
      };
      building: {
        label: string;
        placeholder: string;
      };
      roomNumber: {
        label: string;
        placeholder: string;
      };
      phone: {
        label: string;
        placeholder: string;
        helpText: string;
      };
      changePassword: string;
      deleteAccount: string;
      loading: string;
      alerts: {
        success: string;
        error: string;
        userNotFound: string;
        validationError: string;
        deleteAccount: {
          title: string;
          message: string;
          cancel: string;
          confirm: string;
          finalConfirmation: {
            title: string;
            message: string;
            cancel: string;
            confirm: string;
          };
          success: {
            title: string;
            message: string;
            ok: string;
          };
          failure: string;
        };
      };
    };
    blockedUsers: {
      title: string;
      empty: {
        title: string;
        message: string;
      };
      loading: string;
      loadingUsers: string;
      userNotFound: string;
      unblock: string;
      unableToLoad: string;
      blockedUsersCount: string;
      tryAgainLater: string;
      noBlockedUsers: string;
      noBlockedUsersMessage: string;
      manageBlockedUsersDescription: string;
      unblockConfirmationTitle: string;
      unblockConfirmationMessage: string;
      alerts: {
        unblock: {
          title: string;
          message: string;
          cancel: string;
          confirm: string;
        };
        error: string;
      };
    };
    managePublicProfile: {
      title: string;
      loading: string;
      failedToLoadProfileData: string;
      noProfileDataFound: string;
    };
    support: {
      title: string;
    };
    components: {
      profileScreenHeader: {
        save: string;
        saving: string;
      };
      profileVersionInfo: {
        appVersion: string;
        bundleVersion: string;
      };
      managePublicProfile: {
        header: {
          title: string;
          save: string;
          saving: string;
        };
        aboutSection: {
          title: string;
          description: string;
          placeholder: string;
        };
        skillsSection: {
          title: string;
          description: string;
          characterLimit: string;
          helpingSkills: {
            title: string;
            placeholders: string[];
          };
          needingHelpSkills: {
            title: string;
            placeholders: string[];
          };
          taskNumber: string;
        };
        publicProfile: {
          about: {
            title: string;
          };
          skills: {
            title: string;
            helpingSkills: {
              title: string;
            };
            needingHelpSkills: {
              title: string;
            };
          };
          header: {
            managePublicProfile: string;
            message: string;
            membershipSuffix: string;
          };
        };
      };
    };
  };
  appShell: {
    tabs: {
      feed: string;
      events: string;
      groups: string;
      marketplace: string;
      places: string;
      perks: string;
      profile: string;
    };
  };
  branding: {
    /** Leading half of the badge; "Cobu" is rendered separately in bold. */
    poweredBy: string;
    name: string;
  };
  pwa: {
    updatePrompt: string;
    errorOccurred: string;
  };
  sessionProvider: {
    authError: string;
    initializing: string;
    pleaseWait: string;
  };
  restriction: {
    actionUnavailable: string;
  };
  sessionErrors: {
    jwtDecodeFailed: string;
    jwtExpired: string;
    jwtInvalid: string;
    emailMismatch: string;
    partnerNotFound: string;
    partnerInactive: string;
    partnerConfigError: string;
    buildingNameRequired: string;
    buildingNotFound: string;
    buildingMismatch: string;
    userNoBuilding: string;
    // Omit to fall back to RESOLVE_ERROR_DEFINITIONS[code].userMessage (see resolveErrors.ts),
    // which is the single source of truth for the English copy. Only set this to override
    // with a translation, as Strings.es.ts does.
    leaseInactive?: string;
    activeLeaseElsewhere?: string;
    externalAuthError: string;
    networkError: string;
    native: {
      serverDown: string;
      authFailed: string;
      invalidAccess: string;
      pageLoadFailed: string;
      missingNativePermission: string;
    };
  };
  places: {
    addAPlace: string;
    emptyList: string;
    genericError: string;
    categories: {
      all: string;
      food: string;
      coffee: string;
      outdoor: string;
      shop: string;
    };
    categoriesShort: {
      food: string;
      coffee: string;
      outdoor: string;
      shop: string;
    };
    sort: {
      mostLiked: string;
      mostCommented: string;
      newest: string;
      oldest: string;
    };
    filterAria: string;
    sortAria: string;
    add: {
      title: string;
      nameLabel: string;
      namePlaceholder: string;
      streetLabel: string;
      streetPlaceholder: string;
      cityLabel: string;
      cityPlaceholder: string;
      zipLabel: string;
      zipPlaceholder: string;
      categoryLabel: string;
      descriptionLabel: string;
      descriptionPlaceholder: string;
      shareToFeedTitle: string;
      shareToFeedSubtitle: string;
      defaultShareComment: string;
      submit: string;
      addedToast: string;
      duplicateTitle: string;
      duplicateView: string;
      duplicateAddAnyway: string;
    };
    detail: {
      addedBy: string;
      openInMaps: string;
      share: string;
      comment: string;
      likes: string;
      been: string;
      notFound: string;
      quickPrompts: {
        wantToGo: string;
        question: string;
        recommend: string;
      };
    };
    people: {
      likesTitle: string;
      beenTitle: string;
      empty: string;
    };
    chat: {
      heading: string;
      empty: string;
      addComment: string;
      delete: string;
    };
    compose: {
      title: string;
      post: string;
      placeholder: string;
    };
    share: {
      title: string;
      types: {
        wantToGo: string;
        question: string;
        recommend: string;
      };
      placeholders: {
        wantToGo: string;
        question: string;
        recommend: string;
      };
      post: string;
      postedToast: string;
    };
    report: {
      menuLabel: string;
      cancel: string;
      title: string;
      submit: string;
      body: string;
      placeholder: string;
      successTitle: string;
      successBody: string;
      errorTitle: string;
      errorBody: string;
    };
    embed: {
      tapToSee: string;
    };
    validation: {
      nameRequired: string;
      nameTooLong: string;
      streetRequired: string;
      cityRequired: string;
      zipRequired: string;
      categoryRequired: string;
      descriptionTooLong: string;
      reportReasonRequired: string;
      reportReasonTooLong: string;
    };
  };
}

// Default strings used in the app (English)
export const defaultStrings: StringRepository = {
  common: {
    buttonLabels: {
      login: 'LOGIN',
      loginLoading: 'Logging in...',
      signup: 'SIGN UP',
      logout: 'LOGOUT',
      continue: 'CONTINUE',
      confirm: 'CONFIRM',
      cancel: 'CANCEL',
      done: 'DONE',
      showAll: 'Show all',
      goBack: 'Go Back',
      tryAgain: 'Try Again',
      ok: 'OK',
      yes: 'Yes',
      no: 'No',
      save: 'Save',
      send: 'Send',
      share: 'Share',
      join: 'Join',
      leave: 'Leave',
      message: 'Message',
      back: 'Back',
      post: 'Post',
    },
    navigationLabels: {
      back: 'Back',
      next: 'Next',
      cancel: 'Cancel',
    },
    inputLabels: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      password: 'Password',
    },
    placeholders: {
      tapToAddPhoto: 'Tap to add a photo',
      selectGroup: 'Select a group',
      reportReason: 'Please tell us why you are reporting this...',
      gatheringPhotoOptional: 'Tap to add a gathering photo (optional)',
    },
    termsOfService: 'Terms of Service',
    somethingWentWrong: 'Something Went Wrong',
    tryAgain: 'Try Again',
    loading: 'Loading...',
    error: {
      userNotFound: 'User not found. Please check your phone number and try again.',
      updateFailed: 'Update failed. Please try again.',
      linkOpenFailed: 'Failed to open link. Please try again.',
      invalidUrl: 'Invalid URL',
      errorLoadingPage: 'Error Loading Page',
      title: 'Error',
      errorLoadingEvent: 'Error Loading Event',
      somethingWentWrongEventDetails: 'Something went wrong while loading the event details.',
      genericMessage: 'Something went wrong',
    },
    connect: 'Connect',
    chat: 'Chat',
    events: 'Events',
    marketplace: 'Marketplace',
    alerts: {
      cancelGathering: {
        title: 'Aw! Are you sure you want to cancel this gathering?',
        message: 'RSVPers will be notified that the Gathering was canceled.',
      },
    },
  },
  onboarding: {
    welcome: {
      headline: 'Welcome to your community',
      subhead: 'Your private apartment-wide community',
      benefits: {
        gatherings: 'Learn about and attend fun gatherings',
        recommendations: 'Get help and recommendations from neighbors',
        marketplace: 'Buy, sell, and trade in the neighbors-only marketplace',
      },
      conductSummary:
        'Every neighbor here agrees to be thoughtful, constructive, and respectful, and to avoid any harmful activity.',
      termsConsentPrefix: 'I agree to Cobu\'s ',
      termsConsentLink: 'Terms of Service and Code of Conduct',
      termsConsentSuffix: ', and confirm I\'m 18 or older',
      smsConsent:
        'I consent to receive SMS/MMS from Cobu, including reminders, feedback requests, and promotional content. Reply STOP to opt out anytime.',
      cta: 'Join your community',
      ctaLoading: 'Joining...',
      error: 'We couldn\'t save your choices. Please try again.',
    },
  },
  introduction: {
    title: 'Introduce yourself to the {buildingName} Community!',
    subtitle: 'A photo helps make your {buildingName} community a friendlier place.',
    photoSection: {
      title: 'Update your Photo',
      uploadText: 'Upload a photo',
      editText: 'EDIT',
      actionSheetTitle: 'Update your Photo',
      options: {
        chooseFromLibrary: 'Choose from Library',
        takePhoto: 'Take a Photo',
        cancel: 'Cancel',
      },
    },
    introText: 'Hey, everyone. 👋 I\'m {userFirstName}.',
    placeholder: 'Say a few sentences to introduce yourself. You could say, e.g., "I\'ve lived at {buildingName} for 3 months. My favorite taco place is Anna\'s Tacqueria. Our golden retriever is called Spot. I\'m excited to be here and connect with you all"',
    buttonLabels: {
      next: 'NEXT',
      nextLoading: 'Next...',
      skip: 'Skip this step',
    },
    errors: {
      uploadFailed: 'Unable to upload image. Please try again.',
      somethingWentWrong: 'Something went wrong, please try again later',
      cameraPermissionDenied: 'Camera permission is required to take photos.',
      photoLibraryPermissionDenied: 'Photo library permission is required to select photos.',
      calendarPermissionDenied: 'Calendar permission is required to add events to your calendar.',
    },
  },
  perks: {
    title: 'Perks',
    filters: {
      all: 'All',
      saved: 'Saved',
    },
    states: {
      error: {
        title: 'Error loading perks',
        subtitle: 'Please try again later.',
      },
      empty: {
        allPerks: 'No perks available',
        savedPerks: 'No saved perks',
      },
    },
    perkCard: {
      tapToSeeDetails: 'Tap to see details',
    },
    detail: {
      title: 'Perk',
      enjoyedPerk: 'Your neighbors have enjoyed this perk {count} times!',
      actions: {
        saved: 'SAVED',
        saveToList: 'SAVE TO MY LIST',
        learnMore: 'LEARN MORE',
        redeem: 'REDEEM',
        save: 'SAVE',
        email: 'EMAIL',
        phone: 'PHONE',
        redeemed: 'REDEEMED',
      },
    },
  },
  auth: {
    login: {
      title: 'Login',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Password',
      forgotPassword: 'Password help?',
      needSignup: 'Need to sign up?',
      slogan: 'Power genuine community',
    },
    signup: {
      title: 'Sign Up',
      namePlaceholder: 'Full Name',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Password',
      haveAccount: 'Already have an account?',
      inputLabels: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        buildingCode: 'Building Code',
        unit: 'Unit',
        phoneNumber: 'Phone Number',
        password: 'Password',
        repeatPassword: 'Repeat Password',
      },
      checkboxLabels: {
        agreeToTerms: 'I agree with the Terms of Service',
        ageConfirmation: 'I confirm that I am at least 18 years of age',
        smsConsent:
          'I consent to receive SMS/MMS text messages from Cobu at the number provided above in connection with Cobu related services (eg, reminders about property events, property feedback requests). I understand these texts may include promotional content and may be sent using an automated technology. Reply via text [STOP] to opt out at any time.',
      },
      helpText: {
        buildingCode:
          'Please enter your building code so that we can authorize you. If you do not know your building code, please contact your property manager and they can provide you with one.',
        buildingCodeVerified:
          'Your building code is verified!',
        phoneVerification: 'The phone number is needed to verify your account.',
        leaseEmailRequirement:
          'Please use the same email address listed on your lease agreement to sign up—this ensures we can verify your residency and grant you access. Accounts with unmatched emails may be deactivated during our verification process.',
      },
      errors: {
        invalidBuildingCode:
          'Provided building code is not valid. Please try again or check with your property manager!',
        emailTaken:
          'The account associated with this email has either been claimed or is attached to a different last name. Please start over with a different email or contact us at info@livecobu.com if you believe your email is being misappropriated.',
        emailCheckError: 'An error occurred while checking email availability, please try again.',
        phoneTaken:
          'The account associated with this phone number has either been claimed or is attached to a different last name. Please start over with a different phone number or contact us at info@livecobu.com if you believe your phone number is being misappropriated.',
        phoneCheckError: 'An error occurred while checking phone availability, please try again.',
        signupError: 'An error occurred during signup, please try again.',
      },
    },
    passwordReset: {
      title: 'Reset Password',
      subtitle: 'Enter your email to receive a password reset link',
      emailPlaceholder: 'Enter your email address',
      buttonText: 'Send Reset Link',
      buttonLoadingText: 'Sending...',
      successMessage: 'If we find your account in our databases, we will send you an email with instructions on how to reset your password.',
      errorMessage: 'Failed to send password reset link. Please try again.',
    },
    verification: {
      title: 'Verify Your Phone Number',
      subtitle: 'Enter the code we sent you to',
      verifying: 'Verifying...',
      noCode: "Didn't receive a code?",
      resend: 'RESEND',
      codeSent: 'A new verification code has been sent to your phone.',
      wrongPhone: 'Wrong phone number?',
      updateNumber: 'UPDATE NUMBER',
      updatePhoneTitle: 'Update Phone Number',
      updatePhoneSubtitle: 'Enter your new phone number',
      phoneNumberPlaceholder: '(555) 555-5555',
      phoneUpdated: 'Phone number updated successfully!',
      updating: 'Updating...',
      update: 'UPDATE',
      errors: {
        invalidCode: 'The code you entered seems to be invalid. Please try again.',
        verificationFailed: 'Verification failed. Please try again.',
        sendCodeFailed: 'Failed to send verification code. Please try again.',
        invalidPhoneFormat: 'Invalid phone format. Please enter a valid phone number.',
      },
    },
    deactivatedUser: {
      title: 'Account Deactivated',
      mainText: 'Your Cobu account has been deactivated.',
      description: 'For questions, please reach out to:',
      problem: {
        heading: 'What is the problem?',
        body: 'You cannot log in or reset your password.',
      },
      why: {
        heading: 'Why is this Happening?',
        body: 'This typically happens for three reasons. One is inactivity - you have not used Cobu in the past 6 months. The second reason is that your email address on Cobu does not match the email address on file with your property management team. The third reason is that you have moved out of the building.',
      },
      resolve: {
        heading: 'How do you resolve this?',
        body: 'Email customersuccess@livecobu.com and let us know that your Cobu account should still be active! We will look into this for you right away.',
      },
      contactSupport: 'CONTACT SUPPORT',
      supportEmail: 'customersuccess@livecobu.com',
      emailSubject: 'Cobu Account Deactivated',
    },
  },
  home: {
    welcomeMessage: 'Welcome Home',
  },
  groups: {
    loadingMessage: 'Loading groups...',
    myGroups: 'My Groups',
    allGroupsOpenToResidents: 'All Groups are open to all residents',
    otherGroups: 'Other Groups',
    allGroups: 'All Groups',
    emptyState: {
      title: 'You\'re not a member of any groups!',
      subtitle: 'Groups are a great way to connect with people of similar interests.',
    },
    card: {
      memberCount: {
        singular: '{count} member',
        plural: '{count} members',
      },
      eventCount: {
        singular: '{count} gathering',
        plural: '{count} gatherings',
      },
      newBadge: 'NEW',
    },
    suggest: {
      title: 'Suggest a Group',
      subtitle: 'Help us create a group that brings your community together!',
      cardCta: 'Suggest One!',
      form: {
        groupName: {
          label: 'Group Name',
          placeholder: 'What would you like to call this group?',
          helpText: 'Choose a clear, descriptive name for your group.',
        },
        description: {
          label: 'Description',
          placeholder: 'Describe what this group is about...',
          helpText: 'Explain the purpose and activities of this group.',
        },
        additionalDetails: {
          label: 'Additional Details (Optional)',
          placeholder: 'Any extra information? (meeting times, specific activities, etc.)',
          helpText: 'Add any other details that might help us create the perfect group.',
        },
      },
      buttons: {
        submit: 'SUBMIT SUGGESTION',
        submitting: 'Submitting...',
        cancel: 'Cancel',
        backToApp: 'Back to app',
      },
      success: {
        title: 'Thank You!',
        message: 'We\'ve submitted your request and you\'ll be notified when the group is created. Thanks so much!!',
      },
      chat: {
        robotName: 'Cobu',
        responses: {
          step1: 'Awesome! What group do you suggest?',
          step2Excellent: 'Excellent',
          step2Question: 'How would you describe this group?',
          step2Note: "(This will be the group's details, you can edit this later)",
          step3Question: 'Is there any other information you would like to add? (For example activities or meeting times)',
          step4Fabulous: 'Fabulous!',
        },
        placeholders: {
          groupName: 'group name',
          groupDescription: 'group description',
          extraDetails: 'extra details',
        },
        errorResponse: 'Sorry, there was an error submitting your suggestion. Please try again.',
      },
      errors: {
        submitFailed: 'Failed to submit group suggestion. Please try again.',
        groupExists: 'Sorry, this group already exists. Please try another name.',
        networkError: 'Network error. Please check your connection and try again.',
      },
    },
    info: {
      title: 'Info',
      about: 'About',
      members: 'Members',
      pastGatherings: 'Past Gatherings',
      showMore: 'Show More',
      showLess: 'Show Less',
      noDescription: 'No description available.',
      noAdditionalInfo: 'No additional information available.',
      communityWide: 'Community-Wide',
      openToAllResidents: 'This group is open to all residents.',
      buttons: {
        join: 'Join',
        joining: 'Joining...',
        leave: 'Leave',
        leaving: 'Leaving...',
      },
      leaveConfirm: {
        title: 'Hold on a sec!',
        message: "You have RSVP'd to one or more upcoming gatherings in this group. Leaving will cancel these RSVPs. Do you still wish to leave?",
      },
      errors: {
        joinFailed: 'Failed to join group. Please try again.',
        leaveFailed: 'Failed to leave group. Please try again.',
        loadInfoFailed: 'Failed to load group information.',
      },
    },
    errors: {
      loadFailed: 'Failed to load groups',
      refreshFailed: 'Failed to refresh groups',
    },
  },
  chat: {
    navigation: {
      fallbackTitle: 'Chat',
      profile: 'Profile',
      newMessage: 'New Message',
      channelList: 'Messages',
      directMessages: 'Direct Messages',
    },
    errors: {
      unableToLoadChat: 'Unable to load chat. Please try again.',
      streamInitFailed: 'Failed to initialize chat. Please try again.',
      channelLoadFailed: 'Failed to load channels. Please try again.',
      unableToLoadProfile: 'Unable to load profile. Please try again.',
      messageSendFailed: 'Failed to send message. Please try again.',
    },
    analytics: {
      source: 'chat_tab',
    },
    channelList: {
      title: 'Chats',
      noChannels: 'No messages yet. Start a conversation!',
      loading: 'Loading messages...',
      refresh: 'Pull to refresh',
      newMessage: 'New Message',
      error: 'Error',
      chatNotAvailable: 'Chat is not available at the moment. Please try again later.',
      failedToOpenConversation: 'Failed to open conversation. Please try again.',
    },
    channel: {
      typing: 'is typing...',
      messageInputPlaceholder: 'Type a message...',
      sendButton: 'Send',
      reactions: {
        like: 'Like',
        love: 'Love',
        smile: 'Smile',
        laugh: 'Laugh',
        clap: 'Clap',
      },
      webFallback: {
        title: 'Message',
        message: 'Direct messaging is not available on web. Please use the mobile app.',
      },
      loading: {
        conversation: 'Loading conversation...',
      },
      bannedUser: {
        warning: 'This user has been banned. You cannot send messages.',
      },
      reportUser: {
        title: 'Report User',
        notImplementedMessage: 'This feature will be implemented to match the legacy ReportUserModal functionality.',
      },
      alerts: {
        channelMuted: 'Channel muted.',
        channelUnmuted: 'Channel unmuted.',
      },
      menu: {
        muteConversation: 'Mute conversation',
        unmuteConversation: 'Unmute conversation',
        blockUser: 'Block user',
        unblockUser: 'Unblock user',
      },
    },
    actions: {
      report: 'Report',
      block: 'Block User',
      unblock: 'Unblock User',
      delete: 'Delete',
      edit: 'Edit',
      reply: 'Reply',
      muteUser: 'Mute User',
      unmuteUser: 'Unmute User',
    },
  },
  discussion: {
    welcomeMessage: {
      greeting: "Hey, everyone. 👋 I'm {firstName}."
    },
    allMembers: {
      title: 'People in your building',
      subtitle: 'Member',
    },
    alerts: {
      titles: {
        error: "Error",
        success: "Success"
      },
      error: {
        deleteComment: "Failed to delete comment. Please try again.",
        flagComment: "Failed to flag comment. Please try again.",
        blockUser: "Failed to block user. Please try again."
      },
      success: {
        flagComment: "Comment has been flagged for review.",
        blockUser: "User has been blocked successfully."
      }
    },
    commentActions: {
      deleteConfirm: {
        title: "Are you sure you want to delete this comment?",
        message: "Comment won't be visible after you delete it."
      },
      blockConfirm: {
        title: "Block {displayName}?",
        message: "Once you block the user, you won't see their posts, comments, or profile across the app."
      },
      flagConfirm: {
        title: "Are you sure you want to flag this post?",
        message: "By flagging this post, VCM will receive a notification and try to react as quickly as possible."
      },
      labels: {
        deleteComment: "Delete Comment",
        blockUser: "Block the User",
        reportUser: "Report the User",
        flagPost: "Flag the Post",
        cancel: "Cancel"
      }
    },
    title: 'Discussion',
    emptyState: {
      title: 'No messages to show',
      subtitle: 'Messages will appear here',
      createFirstPost: 'Start conversation',
    },
    loadingMessage: 'Loading discussion...',
    comments: {
      singular: '{count} comment',
      plural: '{count} comments',
    },
    buttons: {
      addComment: 'Add a Comment',
      createNewPost: '+ Add a New Post',
      reply: 'Reply',
      edit: 'Edit',
      delete: 'Delete',
      report: 'Report',
      pin: 'Pin',
      unpin: 'Unpin',
      showMore: 'Show More...',
      showLess: 'Show Less...',
      loadMore: 'Load More Comments',
      loadOlder: 'Load Older Comments',
      seeMoreReplies: 'See {count} more replies...',
      hideReplies: 'Hide replies',
    },
    actions: {
      commenting: 'Adding comment...',
      replying: 'Replying...',
      loading: 'Loading...',
      posting: 'Posting...',
      deleting: 'Deleting...',
      reporting: 'Reporting...',
    },
    reactions: {
      like: 'Like',
      love: 'Love',
      clap: 'Clap',
      smile: 'Smile',
      laugh: 'Laugh',
      addReaction: 'Add reaction',
      viewReactions: 'View reactions',
      react: 'React',
      allReactions: 'All reactions',
      reactionCount: {
        singular: '{count} reaction',
        plural: '{count} reactions',
      },
    },
    compose: {
      placeholder: 'Leave a comment...',
      replyPlaceholder: 'Write a reply...',
      characterLimit: '{current}/{max} characters',
      imageLimit: 'Maximum {max} images allowed',
      addImage: 'Add image',
      removeImage: 'Remove image',
    },
    validation: {
      commentEmpty: 'Comment cannot be empty',
      commentNotFound: 'Comment not found',
    },
    success: {
      commentCreated: 'Comment posted successfully',
      replyCreated: 'Reply posted successfully',
      commentUpdated: 'Comment updated successfully',
      commentDeleted: 'Comment deleted successfully',
      commentPinned: 'Comment pinned',
      commentUnpinned: 'Comment unpinned',
      commentFlagged: 'Comment flagged for review',
    },
    marketplace: {
      detectedTitle: 'Marketplace content detected',
      detectedMessage: 'Do you want to post this comment in the Marketplace?',
      confirmYes: 'Yes',
      confirmNo: 'No',
      composeButton: 'Post an Item',
      composeHeader: 'Post an Item',
      composePlaceholder: "Sell or give away an item, or let neighbors know what you're looking for…",
    },
    errors: {
      loadFailed: 'Failed to load comments. Please try again.',
      createFailed: 'Failed to create comment. Please try again.',
      updateFailed: 'Failed to update comment. Please try again.',
      deleteFailed: 'Failed to delete comment. Please try again.',
      reactionFailed: 'Failed to add reaction. Please try again.',
      reportFailed: 'Failed to report content. Please try again.',
      networkError: 'Network error. Please check your connection.',
      permissionDenied: 'You don\'t have permission to perform this action.',
      moderationRequired: 'Your comment is under review.',
      commentTooLong: 'Comment is too long. Please shorten it.',
      tooManyImages: 'Too many images. Please remove some.',
      invalidContent: 'Comment contains invalid content.',
      togglePinFailed: 'Failed to toggle pin status',
      flagCommentFailed: 'Failed to flag comment',
    },
    moderation: {
      flaggedContent: 'This content has been flagged for review.',
      moderationRequired: 'Your comment requires moderation.',
      contentRemoved: 'Comment deleted by user',
      userBlocked: 'User blocked successfully.',
      reportSubmitted: 'Report submitted successfully.',
      imageHiddenTooltip:
        'Image not posted: possible Code of Conduct or privacy concern',
    },
    moderationAlerts: {
      profanity: {
        title: 'Profane language detected!',
        message:
          'Expletives are not permitted on the Cobu platform. Please edit your comment.',
        options: {
          viewCodeOfConduct: 'View Code of Conduct',
          editPost: 'Edit Post',
        },
      },
      sentiment: {
        title: 'Want to review this before posting?',
        message:
          "To keep conversations respectful and constructive, and not use Cobu as a forum to complain about your apartment community, we're asking residents to review posts similar to this that may be in violation of Cobu's code of conduct.",
        options: {
          sendAnyway: 'Send Anyway',
          viewCodeOfConduct: 'View Code of Conduct',
          editPost: 'Edit Post',
        },
      },
      flaggedTermsHeader: 'Flagged terms',
    },
    timeAgo: {
      justNow: 'Just now',
      minuteAgo: '1 minute ago',
      minutesAgo: '{count} minutes ago',
      hourAgo: '1 hour ago',
      hoursAgo: '{count} hours ago',
      dayAgo: '1 day ago',
      daysAgo: '{count} days ago',
      weekAgo: '1 week ago',
      weeksAgo: '{count} weeks ago',
    },
    accessibility: {
      commentBy: 'Comment by {userName}',
      replyTo: 'Reply to {userName}',
      reactToComment: 'React to comment',
      moreOptions: 'More options',
      hidePinnedComment: 'Hide pinned comment',
      expandReplies: 'Expand replies',
      collapseReplies: 'Collapse replies',
    },
  },
  groupMembers: {
    title: 'Members',
    loading: 'Loading members...',
    viewAll: 'Members',
    memberCount: {
      singular: '{count} member',
      plural: '{count} members',
    },
    errors: {
      loadFailed: 'Failed to load members. Please try again.',
      networkError: 'Network error. Please check your connection.',
    },

  },
  tooltip: {
    loading: 'Loading tooltip...',
    ctaPressed: 'Tooltip action executed',
    defaultCta: 'Learn More',
    errors: {
      loadFailed: 'Failed to load tooltip. Please try again.',
      actionFailed: 'Failed to execute tooltip action. Please try again.',
    },
  },
  publicProfile: {
    loading: 'Loading profile...',
    membershipAge: {
      anHour: 'an hour',
      onCobu: 'on Cobu',
      hour: 'hour',
      hours: 'hours',
      day: 'day',
      days: 'days',
      month: 'month',
      months: 'months',
      year: 'year',
      years: 'years',
    },
    buttons: {
      manageProfile: 'Manage Public Profile',
      message: 'Message',
      block: 'Block User',
      report: 'Report User',
      cancel: 'Cancel',
    },
    sections: {
      about: 'About',
      skills: 'Ask for Help Down the Hall',
      skillsSubtitle: 'TASKS WHERE I CAN LEND A HELPING HAND',
      helpingHand: 'TASKS WHERE I CAN LEND A HELPING HAND',
      needHelp: 'TASKS WHERE I COULD USE A HELPING HAND:',
      groups: 'Groups',
      groupsInCommon: 'Groups in common',
      alsoPart: 'Also part of',
    },
    actions: {
      reportUser: 'Report User',
      blockUser: 'Block User',
      blockConfirmTitle: 'Block {displayName}?',
      blockConfirmMessage: 'Once you block the user, you should not be able to see anything posted by that user or see that user\'s profile. Also, you won\'t see:\n\n• Comments and replies in Groups and Gatherings.\n• Comments and replies in Chat.\n• Gatherings created by this user.',
      userBlocked: 'User blocked',
      blockingUser: 'Blocking user...',
      reportSubmitted: 'Report submitted successfully',
    },
    errors: {
      loadFailed: 'Something went wrong with fetching this user.',
      blockFailed: 'Something went wrong, please try again later',
      reportFailed: 'Failed to report user. Please try again.',
      userNotFound: 'User not found',
    },
    accessibility: {
      profileImage: 'Profile image of {displayName}',
      moreOptions: 'More options for {displayName}',
      closeModal: 'Close image viewer',
    },
    imageModal: {
      closeLabel: 'Close',
      profileAlt: 'Profile',
    },
  },
  groupDetail: {
    navigation: {
      loading: 'Loading...',
      groupNotFound: 'Group Not Found',
    },
    actions: {
      joinGroup: 'JOIN THIS GROUP',
      joining: 'Joining group...',
      leaving: 'Leaving group...',
      joined: 'JOINED',
      composeComment: 'Add a Comment',
      replyToComment: 'Reply to comment in {groupName}',
      createNewPost: 'Create new post in {groupName}',
    },
    labels: {
      communityWideGroup: 'COMMUNITY-WIDE GROUP',
      gatherings: 'Gatherings',
      new: 'New Gathering',
      now: 'NOW',
      upcoming: 'UPCOMING GATHERINGS',
      createGathering: 'Create your gathering!',
      gatheringsEmptyTitle: 'Gatherings',
      gatheringsEmptySubtitle: 'Gatherings help bring your neighbors in the {groupName} together in-person or virtually. Create one today.',
      gatheringStatus: {
        past: 'PAST',
        live: 'LIVE',
        upcoming: 'UPCOMING',
      },
      gatheringTime: {
        today: 'Today',
        tomorrow: 'Tomorrow',
      },
      attendance: {
        singular: '{count} attending',
        plural: '{count} attending',
        youreGoing: "You're going",
      },
    },
    states: {
      errorTitle: 'Error',
      somethingWentWrong: 'Something went wrong',
      groupNotFound: 'Group not found',
      groupNotFoundDescription: 'This group may have been removed or you may not have access to it.',
      tryAgain: 'Try Again',
      ok: 'OK',
    },
    errors: {
      joinFailed: 'Failed to join group. Please try again.',
      leaveFailed: 'Failed to leave group. Please try again.',
    },
    analytics: {
      groupJoinedSuccessfully: 'group_joined_successfully',
      groupLeftSuccessfully: 'group_left_successfully',
      leaveGroupInitiated: 'leave_group_initiated',
    },
  },
  forceUpdate: {
    title: 'Your app needs to be updated.',
    subtitle: 'For security and performance reasons, we need you to update the Cobu app to the latest version.',
  },
  maintenanceMode: {
    title: "Cobu is down for maintenance. We'll be back shortly.",
    subtitle: 'Thanks for your patience.',
  },
  codePush: {
    progressMessage: 'Updating app for a smoother experience',
  },
  events: {
    error: {
      title: 'Error loading events',
      message: 'Something went wrong',
      buttonText: 'Try Again',
    },
    detail: {
      fallbackTitle: 'Event',
      buttons: {
        findGatherings: 'FIND GATHERINGS',
        rsvp: 'RSVP',
        payToRsvp: 'Pay to RSVP - {amount}',
        expressInterest: 'Express Interest',
        register: 'Register',
        attending: 'ATTENDING',
        interested: 'INTERESTED',
        change: 'CHANGE',
        rsvpNow: 'RSVP Now',
        creating: 'Creating...',
        updating: 'Updating...',
        createEvent: 'Create Event',
        updateEvent: 'Update Event',
        checkedIn: 'CHECKED IN',
        addGuests: '+ Guests',
        minus: '-',
        plus: '+',
        confirm: 'Confirm',
        cancel: 'Cancel',
        updateRegistration: 'Update Registration',
        updateRsvp: 'Update RSVP',
        confirmCheckin: 'Confirm Check-in',
        confirmRegistration: 'Confirm Registration',
        completeRsvp: 'Complete RSVP',
      },
      alerts: {
        eventCancelled: 'Event Cancelled',
        eventCancelledMessage: 'This event has been cancelled.',
        eventEnded: 'Event Ended',
        eventEndedMessage: 'This event has already ended.',
        rsvpOptions: 'RSVP Options',
        rsvpOptionsMessage: 'What would you like to do?',
        cancelRSVP: 'Cancel RSVP',
        cannotAttend: 'I cannot attend',
        changeGuestCount: 'Change Guest Count',
        invalidCount: 'Invalid Count',
        invalidCountMessage: 'Guest count must be at least 1.',
        editEvent: 'Edit Event',
        editNotImplemented: 'Edit functionality not implemented yet',
        deleteComment: 'Delete Comment',
      },
      labels: {
        locationTBD: 'Location TBD',
        justNow: 'just now',
        rsvpWillOpenSoon: 'RSVPs for this event will open soon. Stay tuned!',
        eventFull: 'Event Full',
        live: 'LIVE',
        gatheringCancelled: 'This gathering has been cancelled',
        residentOrganized: 'Resident-organized gathering',
        gatheringCreator: 'Gathering Creator',
        attendingCount: 'ATTENDING',
        userRsvpConfirmation: 'You have RSVPd to this event',
        whatToBring: 'What to bring',
        residentOrganizedDisclaimer: 'This event is organized by residents and not sponsored by the property.',
        about: 'About',
        attendeesCount: 'Attendees ({count})',
        guestSingular: 'guest',
        guestPlural: 'guests',
        moreAttendeeSingular: 'more attendee',
        moreAttendeePlural: 'more attendees',
        howManyGuests: 'How many guests?',
        eventEnded: 'This event has ended',
        checkInGreetingHeading: 'Excellent\nLook forward to\nseeing you!',
        bringingGuestsQuestion: 'Are you bringing any guests?',
        guestsWithYouQuestion: 'How many guests are with you?',
        noMoreGuestsAllowed: 'No more guests allowed. RSVP limit is full',
        cancelConfirmation: 'RSVP cancelled',
        recurringIndicator: 'Recurring',
        priceFrom: 'From',
        directions: 'Directions',
        drivingDirections: 'Driving Directions',
        close: 'Close',
        findingComment: 'Finding your comment…',
      },
      descriptionSuffix: {
        rsvp: 'Please RSVP - it helps us plan better! Plus, you’ll get reminders.',
        expressInterest: 'Please express interest - it helps us plan better! Plus, you’ll get reminders.',
      },
    },
    create: {
      headerTitles: {
        create: 'Create Group Event',
        edit: 'Edit Group Event',
      },
      labels: {
        selectGroup: 'Select Group',
        gatheringName: 'Gathering Name',
        gatheringDateTime: 'Gathering Date/Time',
        location: 'Location',
        description: 'Description',
        whatToBring: 'What to Bring (Optional)',
        totalAttendees: 'Total Number of Attendees',
        maxGuestsPerRSVP: 'Max Additional Guests per RSVP',
        eventPhoto: 'Event Photo (Optional)',
      },
      placeholders: {
        chooseGroup: 'Choose a group for your event',
        gatheringName: 'Ex. Saturday Running',
        dateTime: 'Ex. Saturday, Jul 20 @ 10am',
        location: 'Ex. Boston Common',
        description: 'e.g. Come and enjoy running with us!',
        whatToBring: 'e.g. Bring some snacks and drinks',
        totalAttendees: 'RSVPs can be set from 1 to 10,000',
        maxGuests: 'Guests per RSVP can be 0 to 1,000',
      },
      errors: {
        selectGroup: 'Please select a group for your event.',
        selectDateTime: 'Please select a date and time for your event.',
        createFailed: 'Failed to create group event. Please try again.',
        updateFailed: 'Failed to update group event. Please try again.',
      },
      success: {
        title: 'Success',
        eventCreated: 'Group event created successfully!',
        eventUpdated: 'Group event updated successfully!',
      },
    },
    menu: {
      editDetails: 'Edit Details',
      cancelGathering: 'Cancel Gathering',
      reportGatheringAuthor: 'Report Gathering Author',
      blockGatheringAuthor: 'Block Gathering Author',
      cancel: 'Cancel',
    },
    empty: {
      buttonText: 'Discover Groups',
      attendingTitle: 'You are not attending any events',
      attendingSubtitle: 'Why not explore groups in your building to see what events are happening?',
      pastTitle: 'No gatherings in the past 6 months',
      pastSubtitle: "Why not set up a new gathering in one of the groups you're interested in?",
      upcomingTitle: 'There are no upcoming gatherings',
      upcomingSubtitle: "Why not set up a new gathering in one of the groups you're interested in?",
      discoverGroups: 'Discover Groups',
    },
    comments: {
      title: 'Discussion',
      addComment: 'Add a Comment',
      commentsCount: 'Comments',
      loading: 'Loading comments...',
      noComments: 'No comments yet. Be the first to comment!',
      deletedComment: 'This comment has been deleted.',
      timeFormat: {
        justNow: 'just now',
        minutesAgo: 'm ago',
        hoursAgo: 'h ago',
      },
    },
    validation: {
      title: {
        required: 'Title is required',
        tooLong: 'Wow, your title is intense! Please shorten to 100 characters.',
      },
      startTime: {
        required: 'Start time is required',
        invalid: 'Please select a valid date and time',
      },
      location: {
        required: 'Location is required',
        tooLong: 'Oops! Your location is lengthy! Please shorten to 250 characters!',
      },
      description: {
        required: 'Description is required',
        tooShort: 'Oops! We need at least 20 characters in this section!',
      },
      maxEventAttendees: {
        required: 'Total attendees is required',
        mustBeNumber: 'Field must only contain numbers',
        mustBeInteger: 'Field must be a whole number',
        minimum: 'Field must be 1 or greater',
        maximum: 'Field must be no greater than 10000',
      },
      maxGuests: {
        required: 'Max guests is required',
        mustBeNumber: 'Field must only contain numbers',
        mustBeInteger: 'Field must be a whole number',
        minimum: 'Field must be 0 or greater',
        maximum: 'Field must be no greater than 1000',
        mustBeLessThanAttendees: 'Guest count is not to exceed total number of attendees',
      },
    },
    feedback: {
      header: {
        thanksForOrganizing: 'Thanks for organizing',
        thanksForComing: 'Thanks for coming to',
      },
      rating: {
        sectionTitle: 'HOW WAS YOUR EXPERIENCE?',
        excellent: 'Excellent',
        good: 'Good',
        meh: 'Meh',
      },
      comment: {
        sectionTitle: 'CARE TO TELL US WHY? (OPTIONAL)',
      },
      buttons: {
        didNotAttend: 'I did not attend',
        submit: 'Submit',
      },
    },
    filters: {
      upcoming: 'Upcoming',
      attending: 'Attending',
      past: 'Past',
    },
    list: {
      sectionTitle: 'Gatherings',
      item: {
        rsvpNow: 'RSVP Now',
        attending: 'ATTENDING',
        happeningNow: 'HAPPENING NOW',
        cancelled: 'This gathering has been canceled.',
        personSingularAttending: 'person attending',
        peoplePluralAttending: 'people attending',
        personSingularAttended: 'person attended',
        peoplePluralAttended: 'people attended',
        youAnd: 'You and',
      },
    },
  },
  webView: {
    defaultTitle: 'Web View',
    surveyFallbackTitle: 'Survey',
    errors: {
      loadFailed: 'Failed to load the page. Please check your internet connection and try again.',
      mailAppFailed: 'Failed to open mail app',
      phoneAppFailed: 'Failed to open phone app',
      messagingAppFailed: 'Failed to open messaging app',
      noUrl: 'No URL provided for the web view.',
    },
  },
  universalButton: {
    errors: {
      linkOpenFailed: 'An error occurred while trying to open the link.',
      noReferralOffer: 'No referral offer found',
    },
  },
  disabledOverlay: {
    defaultOverlayText: 'Join this group to see more content',
    defaultLoadingText: 'JOINING...',
  },
  ui: {
    allMembers: {
      noResultsSearch: 'No residents found matching your search.',
      noResults: 'No residents found.',
      membersLabel: 'MEMBERS',
      externalLabel: 'Non-Cobu resident',
      directMessages: {
        unavailable: 'Direct Messages Unavailable',
        notAvailable: 'Chat is not available on this platform.',
        unableToOpen: 'Unable to open chat',
        tryAgain: 'Please try again.'
      },
      noMembersSearch: 'No members found matching your search.',
      noRsvp: 'No one has RSVP\'d to this event yet.'
    },
    imagePicker: {
      permissionRequired: 'Permission Required',
      permissionRequiredMessage: 'Please grant camera and photo library permissions to upload images.',
      goToSettings: 'GO TO SETTINGS',
      addPhoto: 'Add Photo',
      openGallery: 'Open gallery',
      openCamera: 'Open camera',
      removePhoto: 'Remove Photo',
      choosAnOption: 'Choose an option',
      uploading: 'Uploading...',
      errors: {
        pickImageFailed: 'Failed to pick image from library. Please try again.',
        takePictureFailed: 'Failed to take picture. Please try again.',
      },
      placeholders: {
        tapToAddPhoto: 'Tap to add a photo',
      },
    },
    calendar: {
      openSettings: 'Open Settings',
      eventTitleFallback: 'Event',
      addToCalendar: 'ADD TO CALENDAR',
      addingToCalendar: 'ADDING...',
      addedToCalendar: 'ADDED TO CALENDAR',
    },
    dateTimePicker: {
      selectDate: 'Select Date',
      selectTime: 'Select Time',
      selectDateTime: 'Select date and time',
      selectDateTimeTitle: 'Select Date & Time',
      cancel: 'Cancel',
      confirm: 'Confirm',
      chooseDate: 'Choose date',
      chooseTime: 'Choose time',
    },
    imageViewer: {
      closeLabel: 'Close',
    },
    userAvatar: {
      profileAlt: 'User profile',
    },
    pullToRefresh: {
      pull: 'Pull to refresh',
      release: 'Release to refresh',
      refreshing: 'Refreshing...',
    },
    universalButton: {
      fallbackTitle: 'Button',
    },
    modal: {
      searchPeople: 'Search people',
    },
    members: {
      unknownUser: 'Unknown User',
      guest: 'guest',
      guests: 'guests',
    },
    roleBadge: {
      communityManager: 'Community Manager',
      communityManagerShort: 'CM',
      buildingAdmin: 'Building Admin',
      buildingAdminShort: 'Admin',
    },
    floatingButton: {
      addComment: 'Add a Comment',
      addEvent: 'Add an Event',
    },
    reportUser: {
      userReported: 'User reported',
      userReportedMessage: 'Please refresh the page to see the changes.',
      title: 'Report an Issue',
      cancel: 'Cancel',
      submit: 'Submit',
      submitError: 'Something went wrong while submitting your report. Please try again later.',
      validation: {
        reasonRequired: 'Please provide a reason',
        reasonTooLong: 'Reason must be 500 characters or less',
      },
    },
    groupPicker: {
      title: 'Select Group',
      closeLabel: 'Close',
      loading: 'Loading groups...',
      error: 'Error loading groups',
      failedToLoad: 'Failed to load groups. Please try again.',
      noGroupsFound: 'No groups found for your building.',
    },
    googleReviewModal: {
      title: 'Thanks for feedback!',
      description: 'Want to share your opinion about living at the {buildingName} in general? If so - please click here and take 60 seconds to write a building review on Google.',
      buttons: {
        writeReview: 'WRITE REVIEW',
        skip: 'No Thanks, Not now',
      },
      errors: {
        title: 'Error',
        unableToOpenLink: 'Unable to open review link',
      },
    },
  },
  hooks: {
    publicProfile: {
      errors: {
        loadProfileFailed: 'Failed to load profile data',
        alreadyBlocked: 'This user is already blocked.',
        blockFailed: 'Failed to block user',
        userNotAvailable: 'User information not available',
      },
      success: {
        userBlocked: 'User blocked successfully',
      },
    },
    perkDetail: {
      errors: {
        userNotAvailable: 'User information not available',
        redeemFailed: 'Failed to redeem perk. Please try again.',
        saveFailed: 'Failed to save perk. Please try again.',
      },
    },
    imagePickerHook: {
      permissionNeeded: 'Permission needed',
    },
    eventDetail: {
      errors: {
        rsvpFailed: 'Failed to RSVP to event',
        eventFull: 'Sorry! This event just filled up. RSVP is no longer possible.',
        updateRsvpFailed: 'Failed to update RSVP. The event may be at capacity.',
        capacityReached: 'The event may be at capacity.',
        cancelRsvpFailed: 'Failed to cancel RSVP',
        deleteCommentFailed: 'There has been an error with deleting this comment, please try again',
        cancelGatheringFailed: 'Failed to cancel the gathering. Please try again.',
        blockUserFailed: 'Unable to block user at this time',
      },
    },
    managePublicProfile: {
      errors: {
        userNotFound: 'User not found',
        updateFailed: 'Failed to update profile. Please try again.',
      },
      success: {
        profileUpdated: 'Profile updated successfully',
      },
    },
  },
  referral: {
    tabs: {
      offerDetails: 'OFFER DETAILS',
      yourResults: 'YOUR RESULTS',
    },
    yourResults: {
      title: 'Track and boost your referrals!',
      sections: {
        stats: {
          title: 'Track Your Referrals',
          heading: 'Your Stats',
          subtitle: 'See how many people have clicked on your link, and how many are almost residents.',
        },
        customize: {
          title: 'Personalize Your Referral Page',
          heading: 'Customize Your Landing Page',
          subtitle: 'Boost your referral success by sharing why you love living here.',
        },
      },
    },
    stats: {
      labels: {
        leads: 'leads',
        views: 'views',
        leases: 'leases',
      },
      loading: 'Loading referral stats...',
      errorMessage: 'Failed to load referral stats. Please try again.',
      yourLeads: 'Your Leads',
      tableHeaders: {
        who: 'WHO',
        dateReferred: 'DATE REFERRED',
      },
    },
    customize: {
      loading: 'Loading customization options...',
      errorMessage: 'Failed to load customization options. Please try again.',
      description: 'Your friends and contacts will be able to see your landing page. It is recommended that you share things that you love about the property and your over all experience',
      descriptionPrefix: 'You now have a',
      descriptionSuffix: 'custom, unique page, dedicated to you.',
      previewPage: 'Preview your landing page',
      sectionTitle: 'Customize Your Landing Page',
      includeProfilePicture: 'Include your profile picture',
      updateProfileImage: 'Update Profile Image',
      welcomeMessage: 'Welcome message',
      welcomeMessagePlaceholder: 'Leave a comment...',
      previewPageTitle: 'Preview Your Referral Page',
      alertTitles: {
        success: 'Success',
        error: 'Error',
      },
      messages: {
        profileAdded: 'Profile picture updated successfully!',
        profileRemoved: 'Profile picture removed successfully!',
        profileUpdateFailed: 'Failed to update profile picture. Please try again.',
        previewFailed: 'Failed to load preview page. Please try again.',
        welcomeMessageSaved: 'Customized message saved',
        welcomeMessageSaveFailed: 'Failed to save customize message',
      },
    },
    detail: {
      webViewTitle: 'Refer Friends',
      shareTitle: 'Referral',
      shareMessage: 'I love living at {buildingName}, I\'m referring you to live here too! If interested, please take 1 minute to fill out this referral form to get started.',
      loadingMessage: 'Loading referral details...',
      errorTitle: 'Error Loading Referral',
      alertTitle: 'Error',
      buttons: {
        sharing: 'SHARING...',
        shareLink: 'SHARE LINK',
        referNow: 'REFER NOW',
        goBack: 'Go Back',
      },
      errors: {
        shareError: 'Unable to share referral link. Please try again.',
        shareFailure: 'Failed to share referral link. Please try again.',
        referFormError: 'Unable to open referral form. Please try again.',
        loadError: 'Failed to load referral details. Please try again.',
      },
      success: {
        shareCopied: 'Referral link copied to clipboard.',
      },
      empty: {
        title: 'No referral offer right now',
        subtitle: 'Check back later for new ways to earn rewards.',
      },
    },
    offer: {
      rewardSingle: 'You get {resident} when they sign a lease.',
      rewardDouble: 'You get {resident} and your friend gets {friend} when they sign a lease.',
      termsHeading: 'Terms & eligibility',
    },
  },
  notifications: {
    title: 'Notifications',
    emptyState: "You don't have any notifications yet. 😕",
    loadOlder: 'Load Older Notifications',
    markAllAsRead: {
      title: 'Mark All as Read',
      message: 'Are you sure you want to mark all of your unread notifications as read?',
      confirm: 'Yes',
      cancel: 'No',
    },
    groups: {
      today: 'Today',
      yesterday: 'Yesterday',
      older: 'Older',
    },
    messages: {
      allMarkedAsRead: 'All notifications marked as read',
      markAllFailed: 'Failed to mark notifications as read',
      markSingleFailed: 'Failed to mark notification as read',
    },
    templates: {
      postedInGroupHtml: '<strong>{content}</strong> was posted in <strong>{groupName}</strong>.',
      postedInFeedHtml: '<strong>{content}</strong> was posted in <strong>Feed</strong>.',
      neighborPostedInFeed: 'A neighbor posted in the <strong>Feed Tab</strong>.',
      gatheringCreatedHtml: 'A new gathering, <strong>{title}</strong> has been created in your group <strong>{groupName}</strong>',
      reactionToCommentHtml: '<strong>{name}</strong> reacted to your comment {emoji}',
      commentOnGatheringHtml: '<strong>{name}</strong> has commented on your gathering <strong>{title}</strong>',
      gatheringCanceledHtml: 'Aw! The gathering <strong>{title}</strong> scheduled for <strong>{date}</strong>, has been canceled.',
      gatheringTimeChangedHtml: 'The start time for your gathering, <strong>{title}</strong> has been changed.',
      gatheringDateCancelledHtml: 'Aw! The gathering <strong>{title}</strong> scheduled for <strong>{date}</strong>, has been cancelled.',
      gatheringDateCancelledWithNoteHtml: 'Aw! The gathering <strong>{title}</strong> scheduled for <strong>{date}</strong>, has been cancelled with note "{note}".',
      gatheringReminderHtml: 'A gathering you RSVP\'d to <strong>{title}</strong> starting <strong>{relTime}</strong>',
      newGroupCreatedHtml: 'A new group, <strong>{name}</strong>, started in your building - join now!',
      groupProposalApprovedHtml: 'Your suggested group <strong>{title}</strong> has been approved!',
      directMessageReceived: 'You have a new message from {name}',
      referralOfferCreated: 'A new Refer Friends offer has been added for your community. Tap \'Perks\' in Cobu to learn more!',
      newPerkActivatedHtml: 'A new offer from {merchant} has been added for your community. Tap \'Perks\' in Cobu to learn more!✨',
    },
  },
  review: {
    prompt: {
      loading: 'Checking review eligibility...',
      error: 'Unable to check review eligibility. Please try again later.',
      notAvailable: 'Review prompt is not available on this device.',
    },
    fallback: {
      title: 'Thanks for your feedback!',
      message: 'Want to share your opinion about living at the {buildingName} in general? If so - please click here and take 60 seconds to write a building review on Google.',
      buttons: {
        writeReview: 'WRITE REVIEW',
        cancel: 'No Thanks, Not now',
      },
    },
    success: {
      prompted: 'Review prompt shown successfully',
      completed: 'Thank you for your review!',
    },
    errors: {
      eligibilityCheckFailed: 'Failed to check review eligibility. Please try again.',
      updateStatusFailed: 'Failed to update review status.',
      promptFailed: 'Failed to show review prompt. Please try again.',
      fallbackFailed: 'Failed to open review page. Please try again.',
    },
  },
  profile: {
    header: {
      fallbackName: 'Profile',
    },
    sections: {
      account: 'ACCOUNT',
      helpAndSettings: 'HELP & SETTINGS',
    },
    menuItems: {
      manageAccount: 'Manage Account',
      pleaseVerifyAccount: 'Please Verify Account',
      chatWithCobu: 'Ask Cobu',
      support: 'Support',
      manageNotifications: 'Manage Notifications',
      blockedUsers: 'Blocked Users',
      contactUs: 'Contact Us',
      reviewBuilding: 'Review Building',
      residentPortal: 'Resident Portal',
      buildingContactInfo: 'Building Contact Info',
      logOut: 'Log Out',
    },
    switches: {
      showFullName: 'Show Full Name',
    },
    alerts: {
      logout: {
        title: 'Logout',
        message: 'Are you sure you want to log out of Cobu?',
        cancel: 'Cancel',
        confirm: 'LOG OUT',
      },
      error: {
        title: 'Error',
        message: 'An error has occurred. Please try again later.',
      },
      success: {
        title: 'Success',
        message: 'Your action was successful!',
      },
      comingSoon: {
        title: 'Coming Soon',
        message: 'Ask Cobu will be available soon.',
      },
    },
    loading: {
      loadingNotifications: 'Loading notifications...',
    },
    errors: {
      unableToLoadNotifications: 'Unable to load notifications',
      checkConnectionAndRetry: 'Please check your connection and try again.',
      tapToRetry: 'Tap to retry',
      noNotificationTypesAvailable: 'No notification types available',
      contactSupportIfIssuePersists: 'Please contact support if this issue persists.',
      failedToUpdateNotificationSettings: 'Failed to update notification settings. Please try again.',
      failedToToggleDirectMessagePushNotifications: 'An error occurred while toggling direct message push notifications',
    },
    notifications: {
      title: 'Manage Notifications',
      subtitle: 'Manage notifications for Gatherings, Groups and Discussions.',
    },
    changePassword: {
      title: 'Change Password',
      newPassword: {
        label: 'New Password',
        placeholder: 'Enter your new password',
      },
      confirmPassword: {
        label: 'Confirm New Password',
        placeholder: 'Confirm your new password',
      },
      passwordRequirements: {
        title: 'Password Requirements:',
        requirements: [
          '• At least 8 characters long',
        ],
      },
      alerts: {
        validationError: 'Validation Error',
        success: 'Success',
        error: 'Error',
      },
    },
    manageAccount: {
      title: 'Manage Account',
      firstName: {
        label: 'First Name',
        placeholder: 'Enter your first name',
      },
      lastName: {
        label: 'Last Name',
        placeholder: 'Enter your last name',
      },
      email: {
        label: 'Email',
        placeholder: 'Enter your email',
      },
      building: {
        label: 'Building',
        placeholder: 'Building name',
      },
      roomNumber: {
        label: 'Unit',
        placeholder: 'Enter your unit number',
      },
      phone: {
        label: 'Phone Number',
        placeholder: 'XXX XXX XXXX',
        helpText: 'Your phone number is verified and cannot be changed',
      },
      changePassword: 'Change Password',
      deleteAccount: 'Delete Account',
      loading: 'Loading...',
      alerts: {
        success: 'Account updated successfully',
        error: 'Failed to update account. Please try again.',
        userNotFound: 'User not found',
        validationError: 'Please fix the errors in the form',
        deleteAccount: {
          title: 'Request Account Deletion',
          message: 'This action will delete your account. This is permanent and your data will be lost permanently. This will also delete any group events that you created. Once deleted, your account or data cannot be restored or retrieved. If you want to temporary disable your account, you can deactivate your account',
          cancel: 'CANCEL',
          confirm: 'DELETE ACCOUNT',
          finalConfirmation: {
            title: 'Final Confirmation',
            message: 'This will permanently delete your account and all associated data. This action cannot be undone.',
            cancel: 'Cancel',
            confirm: 'Yes, Delete My Account',
          },
          success: {
            title: 'Account Deleted',
            message: 'Your account has been successfully deleted. We hope to see you again in the future.',
            ok: 'OK',
          },
          failure: 'Failed to delete account. Please try again later.',
        },
      },
    },
    blockedUsers: {
      title: 'Blocked Users',
      empty: {
        title: 'No Blocked Users',
        message: 'You haven\'t blocked any users yet.',
      },
      loading: 'Loading...',
      loadingUsers: 'Loading blocked users...',
      userNotFound: 'User not found',
      unblock: 'UNBLOCK',
      unableToLoad: 'Unable to load blocked users',
      blockedUsersCount: 'The blocked users are',
      tryAgainLater: 'Please try again later.',
      noBlockedUsers: 'No blocked users',
      noBlockedUsersMessage: 'You don\'t have any blocked users.',
      manageBlockedUsersDescription: 'Manage users you\'ve blocked from seeing your activities.',
      unblockConfirmationTitle: 'Unblock User',
      unblockConfirmationMessage: 'By unblocking, you will be able to see all user\'s activities.',
      alerts: {
        unblock: {
          title: 'Are you sure you want to unblock {userName}?',
          message: 'By unblocking, you will be able to see all user\'s activities.',
          cancel: 'CANCEL',
          confirm: 'UNBLOCK',
        },
        error: 'Failed to unblock user. Please try again.',
      },
    },
    managePublicProfile: {
      title: 'Manage Public Profile',
      loading: 'Loading...',
      failedToLoadProfileData: 'Failed to load profile data',
      noProfileDataFound: 'No profile data found',
    },
    support: {
      title: 'Support',
    },
    components: {
      profileScreenHeader: {
        save: 'Save',
        saving: 'Saving...',
      },
      profileVersionInfo: {
        appVersion: 'App Version - v',
        bundleVersion: 'Bundle Version - ',
      },
      managePublicProfile: {
        header: {
          title: 'Manage Public Profile',
          save: 'Save',
          saving: 'Saving...',
        },
        aboutSection: {
          title: 'About',
          description: 'Help your neighbors get to know you! Share all the great things that make you, YOU (interests, work life, favorite food, etc.).',
          placeholder: 'Teacher, animal lover, nature enthusiast.',
        },
        skillsSection: {
          title: 'Ask for Help Down the Hall',
          description: 'Everyone has something they are good at! Let us know how you can help your neighbors and what jobs they can help you with.',
          characterLimit: 'Please limit your answers to 100 characters or less.',
          helpingSkills: {
            title: 'THREE TASKS WHERE I CAN LEND A HELPING HAND:',
            placeholders: [
              'I can feed your cat while you\'re away',
              'I can teach you to play the piano',
              'I can carpool to work'
            ],
          },
          needingHelpSkills: {
            title: 'THREE TASKS WHERE I COULD USE A HELPING HAND:',
            placeholders: [
              'I would like to learn French',
              'I would like to learn to be a better cook',
              'I need occasional help lifting heavy things'
            ],
          },
          taskNumber: 'Task Number ',
        },
        publicProfile: {
          about: {
            title: 'About',
          },
          skills: {
            title: 'Ask for Help Down the Hall',
            helpingSkills: {
              title: 'TASKS WHERE I CAN LEND A HELPING HAND',
            },
            needingHelpSkills: {
              title: 'TASKS WHERE I COULD USE A HELPING HAND',
            },
          },
          header: {
            managePublicProfile: 'Manage Public Profile',
            message: 'Message',
            membershipSuffix: ' on Cobu',
          },
        },
      },
    },
  },
  appShell: {
    tabs: {
      feed: 'Feed',
      events: 'Events',
      groups: 'Groups',
      marketplace: 'Marketplace',
      places: 'Places',
      perks: 'Perks',
      profile: 'Profile',
    },
  },
  branding: {
    poweredBy: 'Powered by',
    name: 'Cobu',
  },
  pwa: {
    updatePrompt: 'Update available. Reload now?',
    errorOccurred: 'An error has occurred',
  },
  sessionProvider: {
    authError: 'Authentication Error',
    initializing: 'Initializing your session',
    pleaseWait: 'Please wait while we set things up…',
  },
  restriction: {
    actionUnavailable: 'This action is unavailable while your account is restricted.',
  },
  sessionErrors: {
    jwtDecodeFailed: 'Authentication failed. Please try again.',
    jwtExpired: 'Your session has expired. Please refresh and try again.',
    jwtInvalid: 'Authentication failed. Please try again.',
    emailMismatch: 'Authentication error. Please try again.',
    partnerNotFound: 'Service temporarily unavailable. Please contact support.',
    partnerInactive: 'Service temporarily unavailable. Please contact support.',
    partnerConfigError: 'Service temporarily unavailable. Please contact support.',
    buildingNameRequired: 'Missing building information. Please try again.',
    buildingNotFound: 'Building not found. Please contact support.',
    buildingMismatch: "You don't have access to this building.",
    userNoBuilding: 'Account configuration error. Please contact support.',
    externalAuthError: 'Something went wrong. Please try again.',
    networkError: 'Unable to reach Cobu servers. Please try again.',
    native: {
      serverDown: "We're experiencing a server issue. Please try again shortly.",
      authFailed: 'Authentication failed. Please check your credentials and try again.',
      invalidAccess: "It looks like you don't have access to this module. Please try again later. If this issue persists, please contact support.",
      pageLoadFailed: "The page couldn't load. Please refresh and try again.",
      missingNativePermission: 'Required native permission is missing.',
    },
  },
  places: {
    addAPlace: 'Add a place',
    emptyList: 'No places yet. Be the first to add one your neighbors will love.',
    genericError: 'Something went wrong',
    categories: {
      all: 'All',
      food: 'Food & drink',
      coffee: 'Coffee & cafes',
      outdoor: 'Outdoors',
      shop: 'Shopping',
    },
    categoriesShort: {
      food: 'Food',
      coffee: 'Coffee',
      outdoor: 'Outdoors',
      shop: 'Shopping',
    },
    sort: {
      mostLiked: 'Most likes',
      mostCommented: 'Most comments',
      newest: 'Newest',
      oldest: 'Oldest',
    },
    filterAria: 'Filter by category',
    sortAria: 'Sort places',
    add: {
      title: 'Add a place',
      nameLabel: 'Place name',
      namePlaceholder: 'e.g. Groundwork Coffee',
      streetLabel: 'Street address',
      streetPlaceholder: '88 Oak Ave',
      cityLabel: 'City',
      cityPlaceholder: 'Denver',
      zipLabel: 'Zip',
      zipPlaceholder: '80203',
      categoryLabel: 'Category',
      descriptionLabel: 'Why do you love it? (optional)',
      descriptionPlaceholder: 'Tell neighbors what makes it special...',
      shareToFeedTitle: 'Also share to Feed?',
      shareToFeedSubtitle: 'Neighbors see new places in the Feed.',
      defaultShareComment: 'Just added {name} to Places.',
      submit: 'Add place',
      addedToast: 'Place added!',
      duplicateTitle: 'A similar place already exists',
      duplicateView: 'View it',
      duplicateAddAnyway: 'Add anyway',
    },
    detail: {
      addedBy: 'Added by {name}',
      openInMaps: 'Open in Google Maps',
      share: 'Share',
      comment: 'Comment',
      likes: 'likes',
      been: 'been',
      notFound: 'This place could not be found',
      quickPrompts: {
        wantToGo: 'Anyone want to go?',
        question: 'Ask a question',
        recommend: 'Been here!',
      },
    },
    people: {
      likesTitle: '{count} likes',
      beenTitle: '{count} been',
      empty: 'Nobody yet',
    },
    chat: {
      heading: 'Neighbor chat',
      empty: 'No comments yet — be the first to say something.',
      addComment: 'Add a comment',
      delete: 'Delete',
    },
    compose: {
      title: 'Comment',
      post: 'Post',
      placeholder: 'Add to the conversation...',
    },
    share: {
      title: 'Share to feed',
      types: {
        wantToGo: 'Want to go?',
        question: 'Ask a question',
        recommend: 'Recommend it',
      },
      placeholders: {
        wantToGo: 'Anyone want to check this out together?',
        question: 'Do they have specials?',
        recommend: 'Love this place.',
      },
      post: 'Post to feed',
      postedToast: 'Posted to feed',
    },
    report: {
      menuLabel: 'Report place',
      cancel: 'Cancel',
      title: 'Report place',
      submit: 'Submit',
      body: 'Let community admins know if {placeName} is closed, inaccurate, or inappropriate.',
      placeholder: "What's wrong with this place?",
      successTitle: 'Place reported',
      successBody: 'Thanks for letting us know — community admins will take a look.',
      errorTitle: 'Something went wrong',
      errorBody: "We couldn't submit your report. Please try again.",
    },
    embed: {
      tapToSee: 'tap to see in Places',
    },
    validation: {
      nameRequired: 'Place name is required',
      nameTooLong: 'Place name is too long',
      streetRequired: 'Address is required',
      cityRequired: 'City is required',
      zipRequired: 'Zip code is required',
      categoryRequired: 'Choose a category',
      descriptionTooLong: 'Keep it under 500 characters',
      reportReasonRequired: "Please tell us why you're reporting this place",
      reportReasonTooLong: 'Keep it under 500 characters',
    },
  },
};

export type Language = 'en' | 'es';

const LANGUAGE_STORAGE_KEY = 'cobu_language';
const SUPPORTED_LANGUAGES: readonly Language[] = ['en', 'es'] as const;

const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

let currentLanguage: Language = 'en';
// Underlying object — swapped wholesale by setLanguage().
let currentStrings: StringRepository = defaultStrings;

// Proxy that always reads from currentStrings at access time. This is the key
// to avoiding stale captures from `const strings = Strings.get()` at module
// load: even if a module captured the proxy before setLanguage() ran, every
// property access goes back through this trap and reads the live object.
const stringsProxy = new Proxy({} as StringRepository, {
  get(_target, prop, _receiver) {
    return Reflect.get(currentStrings as object, prop);
  },
  has(_target, prop) {
    return Reflect.has(currentStrings as object, prop);
  },
  ownKeys() {
    return Reflect.ownKeys(currentStrings as object);
  },
  getOwnPropertyDescriptor(_target, prop) {
    return Reflect.getOwnPropertyDescriptor(currentStrings as object, prop);
  },
});

/**
 * Read the cached language from localStorage. Returns null if unset/invalid.
 * Synchronous and safe to call before React renders.
 */
const readCachedLanguage = (): Language | null => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
    return isLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Read the language hint from the URL (?language=es or ?lang=es).
 * Synchronous and safe to call before React renders.
 */
const readUrlLanguage = (): Language | null => {
  try {
    if (typeof location === 'undefined') return null;
    const params = new URLSearchParams(location.search);
    const raw = params.get('language') || params.get('lang');
    return isLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Resolve the initial language at app boot, synchronously.
 * Priority: URL param > localStorage cache > default 'en'.
 */
export const resolveInitialLanguage = (): Language =>
  readUrlLanguage() ?? readCachedLanguage() ?? 'en';

/**
 * Switch the active language. For 'es', this dynamically imports the Spanish
 * dictionary so English-only users never download it. Resolves once the
 * language is fully active. No-op if already on the requested language.
 *
 * Persists to localStorage so subsequent boots are synchronous.
 */
const setLanguage = async (lang: Language): Promise<void> => {
  if (!isLanguage(lang)) {
    logger.warn(`[Strings] Unsupported language "${lang}", staying on "${currentLanguage}"`);
    return;
  }
  if (lang === currentLanguage) return;

  if (lang === 'es') {
    const { spanishStrings } = await import('./Strings.es');
    currentStrings = spanishStrings;
  } else {
    currentStrings = defaultStrings;
  }
  currentLanguage = lang;

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }
  } catch {
    // Swallow storage failures (private mode, quota, etc.). Language is set
    // for this session even if we can't persist.
  }
};

/**
 * Helper to get strings from the repository
 */
export const Strings = {
  /**
   * Get the current strings. The returned object is a Proxy that always
   * reads from the active language, so it's safe to capture once at module
   * load and use across language switches.
   */
  get: (): StringRepository => stringsProxy,

  /**
   * Get the active language code.
   */
  getLanguage: (): Language => currentLanguage,

  /**
   * Switch the active language. Async because the Spanish dictionary is
   * lazy-loaded.
   */
  setLanguage,

  /**
   * Resolve the initial language synchronously from URL/localStorage.
   */
  resolveInitialLanguage,

  /**
   * Format a template string with provided replacements
   * @param template - The template string containing placeholders like {buildingName}
   * @param replacements - Object containing key-value pairs for replacement
   * @returns The formatted string with all placeholders replaced
   */
  format: (template: string, replacements: Record<string, string>): string => {
    return Object.entries(replacements).reduce(
      (formatted, [key, value]) =>
        formatted.replace(new RegExp(`{${key}}`, 'g'), value),
      template
    );
  },

  /**
   * Load strings from a remote source (backend)
   * This is a placeholder for future implementation
   */
  loadFromRemote: async (): Promise<void> => {
    try {
      // Simulating API call to fetch strings
      // In real implementation, this would be:
      // const response = await fetch('your-api-endpoint');
      // const remoteStrings = await response.json();
      // currentStrings = { ...defaultStrings, ...remoteStrings };

      // For now, we'll just use the default strings
      return Promise.resolve();
    } catch (error) {
      logger.error('Failed to load strings from remote:', error);
      currentStrings = defaultStrings;
      return Promise.reject(error);
    }
  },

  /**
   * Reset strings back to default English
   */
  resetToDefault: () => {
    currentStrings = defaultStrings;
    currentLanguage = 'en';
  },
};

export default Strings;
